import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Boxes, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@/features/analytics/MetricCard';
import { useCan, useHasModule, useSession } from '@/features/auth/session';
import { InsightsToolbar } from '@/features/insights/InsightsToolbar';
import { percentLabel } from '@/features/insights/labels';
import { stockAnalyticsQueryOptions } from '@/features/insights/queries';
import { validateInsightsSearch, type InsightsSearch } from '@/features/insights/search';
import { useInsightsPeriod } from '@/features/insights/useInsightsPeriod';
import { outletsQueryOptions } from '@/features/pos/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatNumber } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

export const Route = createFileRoute('/_app/analytics/stock')({
  validateSearch: validateInsightsSearch,
  component: StockPage,
});

/** Stocks et marges (ADR-024, D-H58) : coût au déstockage, coût inconnu sans recette (R-ANA-03). */
function StockPage() {
  const { t, i18n } = useTranslation(['insights', 'stock', 'common', 'errors']);
  const locale = i18n.language;
  const session = useSession();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadOperational = useCan('report.operational');
  const canReadFinancial = useCan('report.financial');
  const canRead = canReadOperational && canReadFinancial;
  const hasInventory = useHasModule('inventory');
  const hasPos = useHasModule('pos');
  const propertyId = useScopedPropertyId();
  const { preset, custom, range, compare } = useInsightsPeriod(search);

  const setSearch = (next: Partial<InsightsSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  const outlets = useQuery(outletsQueryOptions(propertyId, canRead && hasPos));
  const query = useQuery(
    stockAnalyticsQueryOptions(
      { ...range, compare, propertyId, outletId: search.outletId },
      canRead && hasInventory,
    ),
  );
  const data = query.data;
  const currency = data?.currencyCode ?? session.user.tenant.currencyCode;
  const money = (value: number) => formatAmount(value, locale, currency);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('insights:stock.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const empty = data ? data.byItem.length === 0 && data.productMargins.length === 0 : false;

  return (
    <Container>
      <PageHeader
        title={t('insights:stock.title')}
        subtitle={t('insights:stock.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('insights:title'), link: <Link to="/analytics">{t('insights:title')}</Link> },
          { label: t('insights:stock.title') },
        ]}
        actions={
          <Button variant="secondary" asChild>
            <Link
              to="/reports/$kind"
              params={{ kind: 'stock_consumption' }}
              search={{ period: preset, from: custom.from, to: custom.to, groupBy: 'ITEM' }}
            >
              <Download className="ico" aria-hidden="true" />
              {t('insights:actions.openReport')}
            </Link>
          </Button>
        }
      />

      <div className="stack">
        <Card>
          <CardBody>
            <InsightsToolbar
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) =>
                setSearch({ period: 'CUSTOM', from: value.from, to: value.to })
              }
              compare={compare}
              onCompareChange={(value) => setSearch({ compare: value || undefined })}
              outlets={outlets.data ?? []}
              outletId={search.outletId}
              onOutletChange={(value) => setSearch({ outletId: value })}
            />
          </CardBody>
        </Card>

        {!hasInventory ? (
          <EmptyState
            icon={<Boxes className="ico ico-24" aria-hidden="true" />}
            title={t('insights:stock.title')}
            description={t('insights:noModule.inventory')}
          />
        ) : query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : (
          <>
            <div className="grid grid-4">
              <MetricCard
                label={t('insights:kpi.consumedCost')}
                display={money(data?.totals.consumedCost.value ?? 0)}
                metric={data?.totals.consumedCost}
                loading={query.isPending}
                higherIsBetter={false}
                showDelta={compare}
                footer={t('insights:kpi.consumedFoot')}
              />
              <MetricCard
                label={t('insights:kpi.purchasedCost')}
                display={money(data?.totals.purchasedCost.value ?? 0)}
                metric={data?.totals.purchasedCost}
                loading={query.isPending}
                higherIsBetter={false}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.lostCost')}
                display={money(data?.totals.lostCost.value ?? 0)}
                metric={data?.totals.lostCost}
                loading={query.isPending}
                higherIsBetter={false}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.countVariance')}
                display={money(data?.totals.countVarianceCost.value ?? 0)}
                metric={data?.totals.countVarianceCost}
                loading={query.isPending}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.stockValue')}
                display={money(data?.totals.stockValue ?? 0)}
                metric={
                  data
                    ? { value: data.totals.stockValue, previous: null, deltaPercent: null }
                    : undefined
                }
                loading={query.isPending}
                showDelta={false}
                footer={t('insights:kpi.stockValueFoot')}
              />
              <MetricCard
                label={t('insights:kpi.revenueExclTax')}
                display={money(data?.totals.revenueExclTax.value ?? 0)}
                metric={data?.totals.revenueExclTax}
                loading={query.isPending}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.grossMargin')}
                display={money(data?.totals.grossMargin.value ?? 0)}
                metric={data?.totals.grossMargin}
                loading={query.isPending}
                showDelta={compare}
                footer={
                  data?.totals.grossMarginRate == null
                    ? undefined
                    : `${t('insights:kpi.grossMarginRate')} ${percentLabel(data.totals.grossMarginRate, locale)}`
                }
              />
            </div>

            {query.isPending ? (
              <Skeleton height={260} />
            ) : empty ? (
              <EmptyState
                icon={<Boxes className="ico ico-24" aria-hidden="true" />}
                title={t('insights:empty.stockTitle')}
                description={t('insights:empty.stockBody')}
              />
            ) : (
              <>
                {hasPos && data && data.productMargins.length > 0 ? (
                  <Card>
                    <CardHeader title={t('insights:sections.margins')} />
                    <CardBody density="flush">
                      <TableWrap>
                        <Table compact>
                          <thead>
                            <tr>
                              <Th>{t('insights:table.product')}</Th>
                              <Th align="right">{t('insights:table.quantity')}</Th>
                              <Th align="right">{t('insights:table.revenueExclTax')}</Th>
                              <Th align="right">{t('insights:table.cost')}</Th>
                              <Th align="right">{t('insights:table.margin')}</Th>
                              <Th align="right">{t('insights:table.marginRate')}</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.productMargins.map((row) => (
                              <Tr key={row.productId ?? row.label}>
                                <Td>{row.label}</Td>
                                <Td numeric align="right">
                                  {formatNumber(row.quantity, locale)}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.revenueExclTax)}
                                </Td>
                                <Td numeric align="right">
                                  {row.costKnown && row.cost !== null ? (
                                    money(row.cost)
                                  ) : (
                                    <Badge tone="amber" title={t('insights:table.costUnknownHint')}>
                                      {t('insights:table.costUnknown')}
                                    </Badge>
                                  )}
                                </Td>
                                <Td numeric align="right">
                                  {row.margin === null ? '—' : money(row.margin)}
                                </Td>
                                <Td numeric align="right">
                                  {row.marginRate === null
                                    ? '—'
                                    : percentLabel(row.marginRate, locale)}
                                </Td>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    </CardBody>
                  </Card>
                ) : null}

                <div className="grid grid--two-thirds">
                  <Card>
                    <CardHeader title={t('insights:sections.byItem')} />
                    <CardBody density="flush">
                      <TableWrap>
                        <Table compact>
                          <thead>
                            <tr>
                              <Th>{t('insights:table.item')}</Th>
                              <Th>{t('insights:table.unit')}</Th>
                              <Th align="right">{t('insights:table.consumed')}</Th>
                              <Th align="right">{t('insights:table.consumedCost')}</Th>
                              <Th align="right">{t('insights:table.purchased')}</Th>
                              <Th align="right">{t('insights:table.purchasedCost')}</Th>
                              <Th align="right">{t('insights:table.lost')}</Th>
                              <Th align="right">{t('insights:table.variance')}</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.byItem ?? []).map((row) => (
                              <Tr key={row.itemId}>
                                <Td>
                                  <div>{row.name}</div>
                                  <div className="muted t-12">{row.code}</div>
                                </Td>
                                <Td>{t(`stock:unit.${row.unit}`, { defaultValue: row.unit })}</Td>
                                <Td numeric align="right">
                                  {formatNumber(row.consumedQuantity, locale)}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.consumedCost)}
                                </Td>
                                <Td numeric align="right">
                                  {formatNumber(row.purchasedQuantity, locale)}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.purchasedCost)}
                                </Td>
                                <Td numeric align="right">
                                  {formatNumber(row.lostQuantity, locale)}
                                </Td>
                                <Td numeric align="right">
                                  {formatNumber(row.countVarianceQuantity, locale)}
                                </Td>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader title={t('insights:sections.byCategory')} />
                    <CardBody density="flush">
                      <TableWrap>
                        <Table compact>
                          <thead>
                            <tr>
                              <Th>{t('insights:table.category')}</Th>
                              <Th align="right">{t('insights:table.consumedCost')}</Th>
                              <Th align="right">{t('insights:table.purchasedCost')}</Th>
                              <Th align="right">{t('insights:table.lostCost')}</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.byCategory ?? []).map((row) => (
                              <Tr key={row.category}>
                                <Td>
                                  {t(`stock:category.${row.category}`, {
                                    defaultValue: row.category,
                                  })}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.consumedCost)}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.purchasedCost)}
                                </Td>
                                <Td numeric align="right">
                                  {money(row.lostCost)}
                                </Td>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Container>
  );
}
